import { useState, useRef, useEffect, useCallback } from 'react'
import { usePlayground } from '../store'
import { SceneRenderer } from './SceneRenderer'
import { CanvasToolbar } from './CanvasToolbar'
import { type StructureFilters, FILTER_DEFAULTS } from './CanvasToolbar'
import { refactorRename } from '../api/enApi'
import styles from './GraphPanel.module.css'

export function GraphPanel() {
    const error = usePlayground((s) => s.error)
    const scene = usePlayground((s) => s.scene)
    const source = usePlayground((s) => s.source)
    const isProcessing = usePlayground((s) => s.isProcessing)
    const focusedNodeId = usePlayground((s) => s.focusedNodeId)
    const selectedIds = usePlayground((s) => s.selectedIds)
    const selectNode = usePlayground((s) => s.selectNode)
    const isMobile = usePlayground((s) => s.isMobile)
    const setSource = usePlayground((s) => s.setSource)
    const transform = usePlayground((s) => s.transform)
    const setTransform = usePlayground((s) => s.setTransform)

    const [structureFilters, setStructureFilters] = useState<StructureFilters>({ ...FILTER_DEFAULTS })
    const svgRef = useRef<SVGSVGElement | null>(null)
    const viewportRef = useRef<HTMLDivElement>(null)

    // Fit view when scene structure changes
    const sceneKey = scene ? `${scene.nodes.length}-${scene.edges.length}-${scene.width.toFixed(0)}-${scene.height.toFixed(0)}` : ''
    useEffect(() => {
        if (!scene) return
        const el = viewportRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        const p = 40
        const s = Math.min((rect.width - p * 2) / scene.width, (rect.height - p * 2) / scene.height, 1.5)
        setTransform({ x: (rect.width - scene.width * s) / 2, y: (rect.height - scene.height * s) / 2, scale: s })
    }, [sceneKey])

    // Double-click to edit node name
    const handleNodeDoubleClick = useCallback(async (nodeId: string, newName?: string, oldName?: string) => {
        if (!scene || !newName) return
        const node = scene.nodes.find((n) => n.id === nodeId)
        if (!node) return
        // oldName is provided for actor rename, otherwise use the node's displayName
        const nameToReplace = oldName ?? node.displayName
        if (newName === nameToReplace) return
        const result = await refactorRename(source, nameToReplace, newName)
        if (result.success && result.source) {
            setSource(result.source)
        }
    }, [scene, source, setSource])

    const hasGraph = scene !== null && !error

    return (
        <div ref={viewportRef} className={styles.viewport}>
            {hasGraph ? (
                <SceneRenderer
                    scene={scene}
                    selectedNodeId={focusedNodeId}
                    selectedIds={selectedIds}
                    onNodeClick={selectNode}
                    onNodeDoubleClick={handleNodeDoubleClick}
                    transform={transform}
                    onTransformChange={setTransform}
                    svgRef={svgRef}
                    isSimulating={false}
                    simSpeed={1}
                    structureFilters={structureFilters}
                />
            ) : (
                <div className={styles.empty}>
                    {isProcessing && !error ? (
                        <>
                            <div className={styles.spinner} />
                            <div className={styles.emptyText}>Building diagram...</div>
                        </>
                    ) : (
                        <>
                            <div className={styles.emptyIcon}>{error ? '\u26A0' : '\u25CC'}</div>
                            <div className={styles.emptyText}>{error ? errorHeading(error) : 'No diagram yet'}</div>
                            <div className={styles.emptyDetail}>{error || (isMobile ? 'Switch to Source tab to write EN code' : 'Write EN code on the left, or pick an example above')}</div>
                        </>
                    )}
                </div>
            )}

            {hasGraph && (
                <CanvasToolbar
                    viewportRef={viewportRef}
                    structureFilters={structureFilters}
                    onFiltersChange={setStructureFilters}
                />
            )}
        </div>
    )
}

function errorHeading(error: string): string {
    if (error.startsWith('Lexer Error')) return 'Syntax error'
    if (error.startsWith('Parser Error')) return 'Parse error'
    if (error.startsWith('Type Error')) return 'Type error'
    if (error.startsWith('Validation Error')) return 'Validation error'
    if (error.startsWith('Server error')) return 'Server error'
    if (error.includes('Failed to fetch') || error.includes('NetworkError')) return 'Connection error'
    return 'Error'
}
